/**
 * useExitIntent — trigger a "wait, don't go" modal for at-risk visitors.
 *
 * Rules (per user directive):
 *  - Arm only after 8s on page AND ≥30% scroll depth (avoid empty bounces).
 *  - Desktop: fire when the mouse leaves the viewport through the TOP edge.
 *  - Mobile: fire on FAST upward scroll from mid-page (heuristic: dY > 60px
 *    in <120ms while scrollY > 30% of scrollable height).
 *  - Once per device (localStorage `kelo_exit_shown`).
 *  - Skip if the user already converted (`kelo_lead_submitted === "1"`) or
 *    another dialog/drawer/lead-modal is open.
 *  - Respect prefers-reduced-motion (still fire; motion is minimal anyway).
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "kelo_exit_shown";
const SUBMITTED_KEY = "kelo_lead_submitted";
const MIN_TIME_MS = 8_000;
const MIN_SCROLL = 0.3;

function inBlockingContext() {
  if (typeof document === "undefined") return true;
  if (document.body.dataset.scrollLocked === "1") return true;
  if (document.querySelector("[data-testid=cart-drawer]")) return true;
  if (document.querySelector("[data-lead-modal-open=\"1\"]")) return true;
  if (document.querySelector("[data-exit-modal-open=\"1\"]")) return true;
  return false;
}

function scrollFraction() {
  const doc = document.documentElement;
  const denom = Math.max(1, doc.scrollHeight - doc.clientHeight);
  return (doc.scrollTop || document.body.scrollTop) / denom;
}

export function markExitShown() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch (_e) {
    /* storage disabled */
  }
}

export function useExitIntent({ enabled = true } = {}) {
  const [shouldOpen, setShouldOpen] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    // Already shown on this device? Don't fire again.
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
      if (localStorage.getItem(SUBMITTED_KEY) === "1") return;
    } catch (_e) {
      /* storage disabled — proceed */
    }

    const mountedAt = Date.now();
    let armed = false;
    let disposed = false;

    const fire = () => {
      if (disposed || !armed) return;
      if (inBlockingContext()) return;
      disposed = true;
      armed = false;
      markExitShown();
      setShouldOpen(true);
    };

    // ------------------------------------------------------------------
    // Arm gate: only after 8s AND ≥30% scroll
    // ------------------------------------------------------------------
    const tryArm = () => {
      if (armed) return;
      if (Date.now() - mountedAt < MIN_TIME_MS) return;
      if (scrollFraction() < MIN_SCROLL) return;
      armed = true;
    };
    const armInterval = window.setInterval(tryArm, 500);
    const onScroll = () => tryArm();
    window.addEventListener("scroll", onScroll, { passive: true });

    // ------------------------------------------------------------------
    // Desktop: cursor leaves the viewport through the top edge.
    // Using `mouseleave` on <html> is the most reliable exit-intent signal.
    // ------------------------------------------------------------------
    const onLeave = (e) => {
      if (!armed) return;
      if (e.clientY > 8) return;
      fire();
    };
    document.documentElement.addEventListener("mouseleave", onLeave);
    // Fallback: some browsers deliver a `mouseout` to document with no relatedTarget.
    const onMouseOut = (e) => {
      if (!armed) return;
      if (e.relatedTarget) return;
      if ((e.clientY ?? 0) > 8) return;
      fire();
    };
    document.addEventListener("mouseout", onMouseOut);

    // ------------------------------------------------------------------
    // Mobile: fast upward scroll from >30% depth
    // ------------------------------------------------------------------
    let lastY = window.scrollY;
    let lastT = Date.now();
    const onMobileScroll = () => {
      if (!armed) return;
      const now = Date.now();
      const y = window.scrollY;
      const dY = lastY - y; // positive = user scrolling up
      const dT = now - lastT;
      lastY = y;
      lastT = now;
      if (dT > 0 && dT < 120 && dY > 60 && scrollFraction() > 0.25) {
        fire();
      }
    };
    window.addEventListener("scroll", onMobileScroll, { passive: true });

    return () => {
      disposed = true;
      armed = false;
      window.clearInterval(armInterval);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onMobileScroll);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [enabled]);

  return { shouldOpen, setShouldOpen };
}
